import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Navigate, useNavigate } from "react-router";
import type { AdoptionCandidate, PetCoreFactsUpdate, PetCoreFactsView, PetPrivateSummary, PetSpecies } from "@/shared/contracts";
import { newIdempotencyKey } from "@/shared/api/idempotency";
import { isPhotoUnclear, photoRejectedForSure, photoUnclearText, photoBeforeSend, photoNotReady, photoNotReadyText, photoResultUncertain } from "./photoRetry";
import { PhotoUnknownSelection, type PhotoSubmission } from "./photoUnknownSelection";
import { toApiError, type ApiError } from "@/shared/api/errors";
import { env } from "@/shared/config/env";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { onboardingRoute, useSessionState } from "@/shared/session/onboarding";
import { householdLabel, useCurrentHousehold, useHouseholdLabels } from "@/shared/session/householdContext";
import { Button, Card, Chip, DataOriginBadge, DisabledState, EmptyState, ErrorState, Icon, Page, QueryView, TopBar } from "@/shared/ui";
import { EntryHeading } from "@/features/identity/EntryHeading";
import { PhotoDataUseNote } from "@/features/identity/dataUse/DataUseNote";
import { AdoptFlow } from "./adoptFlow";
import { ResidentPortrait } from "./ResidentPortrait";
import { identityFaceCrop } from "./world/guestStoriesModel";
import { hasOwnImage, speciesName } from "./residentView";
import { SpeciesIllustration } from "./SpeciesIllustration";
import { FactsFields } from "./facts/FactsFields";
import { useHomeTimeZone } from "./facts/factsData";
import { EMPTY_FACTS, FACTS_BUSY_TEXT, factsProblems, factsSaveFailureText, isEmptyFacts, settleFactsConflict, updateFrom, type FactsForm } from "./facts/factsModel";
import { FactsConflictLines } from "./facts/FactsConflict";
import "./pets.css";

/**
 * 自己建宠能选的物种：只给猫、狗（10-03 照片必填以后，上传照片的识别只认猫和狗，选别的提交会被拒，成了死路；I 15:3x）。
 * 领养原创居民不受影响（居民里有兔子、小鸟）。识别支持更多物种以后再放回来。
 */
const SPECIES: Array<{ id: PetSpecies; label: string }> = [
  { id: "cat", label: "猫" },
  { id: "dog", label: "狗" },
];
const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];
/**
 * 带照片建宠物时“照片这会儿放不上去”（不是照片本身不行）的两种，按错误码和原因码一起认（后端 web_pets/recognition.py、
 * schemas/web/pets.py PhotoCheckReason 的说明）：503 UPSTREAM_UNAVAILABLE ＋ recognition_unavailable、
 * 429 RATE_LIMITED ＋ recognition_rate_limited（这一小时放的照片太多）。明确没发出去（before_send）的还要看 retryable：识别没配好时后端标 false，
 * 单列成「现在还放不了」——不给再试，也不给「换一张照片」（换了也一样），只留「先去星球看看」，提交键灰掉（UI-PHOTO-RECOVERY-RETRYABLE-01，I 10-14）。
 * 10-03 起照片必填（用户「不上传照片的也不能跳过注册」）：不再给“先不放照片，继续”，只说过一会儿再试；retry 表示给不给“再试一次”。
 * ERROR-RECOVERY-01 先补 KEEP-10、KEEP-11（总账 P 节第 7 行）：
 * - KEEP-10：非识别 5xx 保留 X-10 原错误展示，未知状态不授权重复写入，别的 429 只说歇一会儿；都不给跳过照片，不出通用错误卡片。
 * - KEEP-11：识别 503 带 details.recovery = "result_unknown"（I 的 SEND-PHASE-1 设计；c84a D1216-1、D1216-2）——已发出、结果不明：
 *   只给“换一张照片”“先去星球看看”两个出口，不给会一直回旧结论的“查看结果”；只有明确 before_send 才允许主人主动新键重试；缺字段或陌生值仍保留未知锁，不造查询。
 * 不是这几种就是 null。
 */
function photoDownNote(error: ApiError, unclearTimes = 1): { text: string; retry: boolean; label: string; exits: boolean; planetOnly?: boolean } | null {
  const code = error.code;
  const reason = error.details?.reason;
  // 10-03（2531 rc1900 卡住；c84a 1046Z D1046-2、1116Z D1116-2）：没认出来是确定终态，“重新识别”换新键；结果不明（503）照实说还没确认，
  // 明确未发送才给“再试一次”；未知结果没有可用查询入口，不能用再次写入代替查询。
  // 两种都给“换一张照片”“先去星球看看”两个出口，不自动循环
  if (code === "MEDIA_REJECTED" && reason === "photo_unclear" && !photoResultUncertain(error)) return { text: photoUnclearText(unclearTimes), retry: true, label: "重新识别", exits: true };
  if (code === "UPSTREAM_UNAVAILABLE" && reason === "recognition_unavailable") {
    if (photoNotReady(error)) return { text: photoNotReadyText(error), retry: false, label: "", exits: true, planetOnly: true };
    if (photoBeforeSend(error)) return { text: "这张照片这次还没送出，可以主动再试一次。填好的都还在。", retry: true, label: "再试一次", exits: true };
    return { text: "这张照片这次没确认好，换一张照片试试，或者先去星球看看。填好的都还在。", retry: false, label: "", exits: true };
  }
  if (code === "RATE_LIMITED" && reason === "recognition_rate_limited") return { text: "这一小时放的照片有点多了，歇一会儿再来。填好的都还在。", retry: false, label: "再试一次", exits: false };
  if (code === "RATE_LIMITED" || error.status === 429) return { text: "点得有点快了，歇一会儿再来。填好的都还在。", retry: false, label: "再试一次", exits: false };
  if ((error.status === 503 || code === "UPSTREAM_UNAVAILABLE") && !error.isCapabilityUnavailable && error.retryable !== false) return { text: "这次的结果还没确认，填好的都还在，请先别重复提交。", retry: false, label: "", exits: false };
  return null;
}

/** 照片必填的理由（不写任何技术词）：照片框下、按钮旁“还缺什么”都用它 */
const PHOTO_WHY = "星球上的大家才认得出 TA";
/** 生日存好了以后先停一会儿，让人看清“生日存好了。”再去接待（I 10-09：至少 1.2 秒，不等主人点） */
const FACTS_SAVED_PAUSE_MS = 1200;

/** 首次入住入口：已有家庭的管理员从独立的添伙伴页面进入。 */
function useOnboardingRedirect(): string | null {
  const session = useSessionState();
  if (env.dataMode !== "live" || !session.data) return null;
  if (!session.data.authenticated) return "/welcome";
  const step = session.data.onboarding?.step;
  return step && step !== "needs_companion" ? onboardingRoute(session.data.onboarding) : null;
}

function PhotoPicker({ photo, onChange }: { photo: File | null; onChange: (file: File | null, error: string | null) => void }) {
  const [preview, setPreview] = useState<string | null>(null);
  useEffect(() => {
    if (!photo) {
      setPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(photo);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);
  return (
    <div className="ps-pet-photo">
      <label className={`ps-pet-photo__stage${preview ? " has-photo" : ""}`}>
        {preview ? <img src={preview} alt="TA 的照片预览" /> : <span className="ps-pet-photo__empty" aria-hidden="true"><span className="ps-pet-photo__camera"><Icon name="camera" size={27} /></span></span>}
        <span className="ps-pet-photo__action"><Icon name="camera" size={17} />{photo ? "换一张照片" : "放入 TA 的照片"}</span>
        <input
          type="file"
          accept={PHOTO_TYPES.join(",")}
          className="visually-hidden"
          id="own-pet-photo"
          aria-label="上传 TA 的照片"
          onChange={(e) => {
            const file = e.target.files?.[0] ?? null;
            if (file && !PHOTO_TYPES.includes(file.type)) onChange(null, "只支持 JPEG / PNG / WebP 图片。");
            else if (file && file.size > MAX_PHOTO_BYTES) onChange(null, "图片太大了（上限 5MB）。");
            else onChange(file, null);
            e.target.value = "";
          }}
        />
      </label>
      {/* 10-03 起照片必填：选了照片不再给“先不放照片”（点照片框可以换一张） */}
      <span className="ps-pet-photo__note">放一张 TA 的照片，{PHOTO_WHY}。上传时会去掉位置信息，只有你和获你允许的家人能看到。</span>
      {/* 照片交给外部图像服务处理照实说（DATA-USE-COPY-01）：次要文字跟在上面那句后面，不抢“放入 TA 的照片”；“查看内容使用说明”原地打开，填好的不丢 */}
      <PhotoDataUseNote align="center" />
    </div>
  );
}

function OwnPetForm({ householdId }: { householdId?: string }) {
  const { pets } = useServices();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  // 物种不预选：默认成“猫”的话，养狗的人不注意就登记成了猫，之后到处是猫的图。
  const [species, setSpecies] = useState<PetSpecies | null>(null);
  // 性别必选（用户 10-08：性别作为建宠必选）、不预选，和物种同一个道理：默认成哪个都会有人没注意就登记错
  const [sex, setSex] = useState<"male" | "female" | null>(null);
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  // 这一张照片连着几次没认出来（换了照片从 0 数起）：第二次起说法把重点放到换一张
  const [unclearTimes, setUnclearTimes] = useState(0);
  // 第 05 项：生日（PET-FACT-01，可选，默认收起）。什么都没填就不多发请求；性别已经在第 04 项、随建宠一起发。
  const [facts, setFacts] = useState<FactsForm>(EMPTY_FACTS);
  const [factsOpen, setFactsOpen] = useState(false);
  const [created, setCreated] = useState<PetPrivateSummary | null>(null);
  const [factsFailed, setFactsFailed] = useState(false);
  const keyRef = useRef(newIdempotencyKey("pet-create"));
  const factsKeyRef = useRef(newIdempotencyKey("pet-facts"));
  // 再添一只时用这个家的时区（同一个家的宠物住同一个家）；首只还没有家，拿不到就按约数说
  const homeTimeZone = useHomeTimeZone(null);
  const photoSession = useSessionState();
  const photoUserId = photoSession.data?.authenticated ? photoSession.data.user?.user_id : null;
  const photoScope = JSON.stringify([photoUserId ?? null, householdId ?? null]);
  const photoSelection = useRef(new PhotoUnknownSelection()).current;
  photoSelection.syncScope(photoScope);
  const approvedPhotoScope = useRef<string | null>(null);
  const [photoChecking, setPhotoChecking] = useState(false);
  useEffect(() => { photoSelection.activate(); return () => photoSelection.dispose(); }, [photoSelection]);
  useEffect(() => { setPhotoChecking(false); }, [photoScope]);
  // 选图时会话还没读回来（刚注册完缓存里还是旧会话，正在重读），记下的是 [null, 这个家庭]；读回来一位已确认的账号、家庭没变：
  // 那时还没有任何账号能用这张照片（发送要先有账号），归给读回来的这位，不然“继续”一直灰、卡住注册（I 10-13）。
  // 从一位账号换成另一位、或换了家庭，照旧要重新选（UI5660：照片不从 A 带到 B）。
  if (photoUserId && approvedPhotoScope.current === JSON.stringify([null, householdId ?? null])) approvedPhotoScope.current = photoScope;
  const photoScopeReady = Boolean(photoUserId) && approvedPhotoScope.current === photoScope;
  async function proceed(pause = 0) {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.session }),
      queryClient.invalidateQueries({ queryKey: ["households"] }),
      pause > 0 ? new Promise((resolve) => setTimeout(resolve, pause)) : null,
    ]);
    navigate("/onboarding/reception?branch=own_pet");
  }
  // 宠物建成以后再存生日；存不上宠物照样建成，停在这里说明，给“再试一次”（同一个键、同一份内容）和“先继续”。
  // 性别已经随建宠写进去了，档案版本不一定还是 0：第一次先读一次拿当前版本，再整体保存（带上同一个性别，不把它清回“不确定”）。
  // 先存再刷新会话：会话一刷新，首次入住页就按新的一步跳走，存不上时的说明就没处显示了。
  // CREATE-PET-RED-01（I 10-09；A 核过后端：同一个幂等键、同一个请求体重放回第一次的结果）：
  // - 第一次拼好的请求体和键一起存下，“再试一次”原样重发，不再重读版本重拼（重拼会变成同键不同体，第一次其实存上了也被说成“没存上”）；
  // - 409 照 settleFactsConflict 认：带了当初的回包、或读到的就是这次要存的，就是存好了；还在处理就说稍等再看、不自己重发——
  //   主人点“再看看”才原样重发同键同体（幂等重放：那次存好了回原结果，还在处理照旧稍等，那次没留下记录就正常存上；I 10-09）；
  //   键被拒、谁都没存：同一份内容换新键；变成了别的：两边并排写出来，等主人选（“用这次填的”才覆盖，“保留现在的”接着去接待）。
  // - 存好了说一句“生日存好了。”，至少停 1.2 秒再去接待（I 10-09）。
  const factsSentRef = useRef<{ body: PetCoreFactsUpdate; key: string } | null>(null);
  const [factsConflict, setFactsConflict] = useState<{ stored: PetCoreFactsView; body: PetCoreFactsUpdate; version: number } | null>(null);
  const [factsBusy, setFactsBusy] = useState(false);
  const [factsSaved, setFactsSaved] = useState(false);
  const saveFacts = useMutation({
    mutationFn: async ({ petId }: { petId: string }) => {
      if (!factsSentRef.current) {
        const current = await pets.facts(petId);
        factsSentRef.current = { body: updateFrom({ ...facts, sex: sex ?? current.sex }, current.version), key: factsKeyRef.current };
      }
      // “再试一次”和还在处理以后的“再看看”都走这里：原样重发第一次的请求体、同一个键
      const sent = factsSentRef.current;
      try {
        return await pets.saveFacts(petId, sent.body, sent.key);
      } catch (err) {
        const settled = await settleFactsConflict(err, sent.body, () => pets.facts(petId));
        if (settled?.kind === "saved") return settled.view;
        // 还在处理的那一态保持到下一次结果出来（“再看看”等回音时不闪回“没存上”）
        setFactsBusy(settled?.kind === "busy");
        if (settled?.kind === "conflict") setFactsConflict({ stored: settled.stored, body: sent.body, version: settled.version });
        if (settled?.kind === "refused") factsSentRef.current = { body: sent.body, key: newIdempotencyKey("pet-facts") };
        throw err;
      }
    },
    onSuccess: () => {
      setFactsSaved(true);
      return proceed(FACTS_SAVED_PAUSE_MS);
    },
    onError: () => setFactsFailed(true),
  });
  // 真冲突时主人点“用这次填的”：同一份内容按服务端给的最新版本再存一次；换了版本就是另一份请求体，换新键（同键换请求体会被拒）
  function saveFactsOver(petId: string, version: number) {
    const sent = factsSentRef.current;
    if (!sent) return;
    factsSentRef.current = { body: { ...sent.body, expected_version: version }, key: newIdempotencyKey("pet-facts") };
    setFactsConflict(null);
    saveFacts.mutate({ petId });
  }
  // 照片必填（10-03）：只在选了照片时才发，带着这张照片
  const create = useMutation({
    mutationFn: ({ photoToSend, attempt }: { photoToSend: File; attempt: PhotoSubmission }) => {
      if (!photoSelection.current(attempt)) throw new Error("PHOTO_SELECTION_STALE");
      return pets.createOwn({ name: name.trim(), species: species as PetSpecies, sex: sex as "male" | "female", photo: photoToSend, householdId }, attempt.key);
    },
    onSuccess: async (summary, { attempt }) => {
      if (!photoSelection.settle(attempt, false)) return;
      if (isEmptyFacts(facts)) {
        await proceed();
        return;
      }
      setCreated(summary);
      saveFacts.mutate({ petId: summary.pet_id });
    },
    // 后端明确拒了（宠物没建成）：下一次是新的提交，换新键；结果不明的（断网、超时）不换，再点一次是重放
    onError: (err, { attempt }) => {
      const apiError = toApiError(err);
      const unresolved = photoResultUncertain(apiError);
      if (!photoSelection.settle(attempt, unresolved)) return;
      if (!unresolved && (photoRejectedForSure(apiError) || photoBeforeSend(apiError))) keyRef.current = newIdempotencyKey("pet-create");
      if (isPhotoUnclear(apiError)) setUnclearTimes((times) => times + 1);
    },
  });
  function sendPhoto(file: File) {
    if (!photoScopeReady || photoChecking || photoSelection.busy || (create.isError && (photoResultUncertain(toApiError(create.error)) || photoNotReady(toApiError(create.error))))) return;
    const attempt = photoSelection.begin(file, keyRef.current);
    if (attempt) create.mutate({ photoToSend: file, attempt });
  }
  function commitPhoto(file: File | null, err: string | null) {
    setPhoto(file);
    setPhotoError(err);
    setUnclearTimes(0);
    approvedPhotoScope.current = file && !err ? photoScope : null;
    keyRef.current = newIdempotencyKey("pet-create");
    if (file && !err && create.isError) create.reset();
  }
  function selectPhoto(file: File | null, err: string | null) {
    if (photoSelection.isPending) return;
    if (!file || err) {
      photoSelection.cancelSelection();
      setPhotoChecking(false);
      if (photoSelection.hasUnknown) { setPhotoError(err); return; }
      commitPhoto(file, err);
      return;
    }
    if (!photoSelection.hasUnknown) {
      photoSelection.cancelSelection();
      setPhotoChecking(false);
      commitPhoto(file, null);
      return;
    }
    setPhotoChecking(true);
    void photoSelection.select(file).then((decision) => {
      if (decision === "stale" || decision === "pending") return;
      setPhotoChecking(false);
      if (decision === "different") commitPhoto(file, null);
      else setPhotoError(decision === "same" ? "这还是同一张照片，原来的结果还没确认。请选另一张。" : "这张照片暂时没核对好，先保留原来的照片和结果，稍后再选。");
    });
  }
  const error = create.error ? toApiError(create.error) : null;
  const photoResultUnknown = error !== null && photoResultUncertain(error);
  // 没配好：再交一次也一样，提交键灰掉，只留「先去星球看看」（UI-PHOTO-RECOVERY-RETRYABLE-01）
  const photoNotReadyNow = error !== null && photoNotReady(error);
  // 带着照片、照片这会儿放不上去（见 photoDownNote）：不说成“信号中断”，原地说清楚；不给跳过照片（10-03 照片必填）
  const photoDown = error !== null && photo !== null ? photoDownNote(error, unclearTimes) : null;
  // 按钮灰着时就在旁边说清楚还缺什么，不让人对着一个点不动的按钮猜。照片排第一项（10-03 必填）。
  // 缺几项就按顺序连起来说：先……，再……；只缺一项时照原来的说法（只缺名字说“先写下 TA 的名字”，只缺物种或性别不带“先”）
  const lacking = [!name.trim() ? "写下 TA 的名字" : null, !species ? "选一下 TA 是什么动物" : null, !sex ? "选一下 TA 是男孩还是女孩" : null].filter((item): item is string => item !== null);
  const missingDetails = !lacking.length ? null : lacking.length === 1 && lacking[0] !== "写下 TA 的名字" ? lacking[0] : `先${lacking.join("，再")}`;
  const missing = photo ? missingDetails : missingDetails ? `先放一张 TA 的照片，再${missingDetails.replace(/^先/, "")}` : `放一张 TA 的照片，${PHOTO_WHY}`;
  const factsIssue = factsProblems(facts).length > 0;
  // 常驻播报区里这会儿要说的那一句（空着就是不说话）
  const factsLive = created && factsSaved ? "生日存好了。" : created && factsBusy ? FACTS_BUSY_TEXT : "";
  if (env.dataMode === "fixture") {
    return (
      <>
        <DisabledState title="演示模式不上传照片">上传在 live 模式下私有存储、校验格式并去掉照片里的位置信息。</DisabledState>
        <Link to="/onboarding/reception?branch=own_pet" className="ps-btn ps-btn--secondary ps-btn--block">
          用演示宠物体验接待
        </Link>
      </>
    );
  }
  return (
    <form
      className="ps-own-pet-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (photo && !missing && !photoError && !factsIssue && !created && !photoResultUnknown) sendPhoto(photo);
      }}
    >
      <div className="ps-own-pet-form__lead">
        <span>01 / 一张照片</span>
        <h2>先让我们看看 TA</h2>
        <p>选一张你喜欢的，{PHOTO_WHY}。</p>
      </div>
      <PhotoPicker
        photo={photo}
        onChange={selectPhoto}
      />
      {photoError ? (
        <p role="alert" className="ps-form-error">
          {photoError}
        </p>
      ) : null}
      <div className="ps-own-pet-form__details">
        <div className="ps-field ps-own-pet-name">
          <label htmlFor="pet-name">02 / TA 叫什么名字？</label>
          <input id="pet-name" className="ps-input" maxLength={24} required value={name} onChange={(e) => setName(e.target.value)} placeholder="写下 TA 的名字" />
        </div>
        <fieldset className="ps-pet-species">
          <legend>03 / TA 是哪一位？</legend>
          <div className="ps-pet-species__choices">
            {SPECIES.map((s) => (
              <label key={s.id} className="ps-pet-species__choice">
                <input type="radio" name="pet-species" value={s.id} checked={species === s.id} onChange={() => setSpecies(s.id)} />
                <span><SpeciesIllustration species={s.id} />{s.label}</span>
              </label>
            ))}
          </div>
          <p className="ps-pet-species__more">其他小伙伴还在路上。</p>
        </fieldset>
        <fieldset className="ps-pet-sex" disabled={create.isPending || saveFacts.isPending || Boolean(created)}>
          <legend>04 / TA 是男孩还是女孩？</legend>
          <div className="ps-pet-sex__choices">
            {(["male", "female"] as const).map((choice) => (
              <label key={choice} className="ps-pet-sex__choice">
                <input type="radio" name="pet-sex" value={choice} checked={sex === choice} onChange={() => setSex(choice)} />
                <span>{choice === "male" ? "男孩" : "女孩"}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <details className="ps-own-pet-facts" open={factsOpen} onToggle={(e) => setFactsOpen(e.currentTarget.open)}>
          <summary>
            05 / 生日
            <small>可选，也可以之后在“TA 的档案”里填</small>
          </summary>
          <FactsFields sex={false} form={facts} onChange={setFacts} homeTimeZone={homeTimeZone} disabled={create.isPending || saveFacts.isPending || Boolean(created)} />
        </details>
      </div>
      <div className="ps-own-pet-next">
        <span className="ps-own-pet-next__icon"><Icon name="chat" size={23} /></span>
        <div>
          <small>下一站 · 初次接待</small>
          <strong>聊聊 TA 的习惯和故事</strong>
          <p>你可以慢慢说，也可以先跳过。</p>
        </div>
      </div>
      {/* 常驻的播报区（CREATE-PET-RED-01，I 10-09）：一直在表单里，平时空着、不占地方（visually-hidden）；要说话时只换里面的字——
          “生日存好了。”（存好了停 1.2 秒再去接待）“还在保存，稍等一下再看。”——读屏礼貌播报，没有动画。
          不用 role=status：表单里“还缺什么”那一行已经是 role=status，同一页再放一个会让按角色找的用例找到两个。 */}
      <p className={factsLive ? "ps-own-pet-missing" : "visually-hidden"} aria-live="polite" aria-atomic="true" data-testid="own-pet-facts-live">
        {factsLive}
      </p>
      {created && factsSaved ? null : created && factsFailed ? (
        // 还在处理（I 10-09）：第一行只写“TA 已经住进来了。”，“还在保存”只由上面的播报区说；这一块不挂 role=alert，免得读屏连着念两遍。
        // 只有真没存上（普通失败、真冲突）才说“生日这次没存上”
        <div className="ps-own-pet-facts__after" role={factsBusy ? undefined : "alert"}>
          <p>{factsBusy ? "TA 已经住进来了。" : "TA 已经住进来了。生日这次没存上，可以之后在“TA 的档案”里补。"}</p>
          {factsConflict ? (
            <FactsConflictLines stored={factsConflict.stored} body={factsConflict.body} />
          ) : !factsBusy && saveFacts.isError ? (
            <p className="ps-muted">{factsSaveFailureText(saveFacts.error)}</p>
          ) : null}
          {factsConflict ? (
            // 真冲突：主人点了“用这次填的”才覆盖；“保留现在的”不再存，接着去接待
            <div>
              <Button variant="secondary" size="sm" loading={saveFacts.isPending} onClick={() => saveFactsOver(created.pet_id, factsConflict.version)}>
                用这次填的
              </Button>
              <Button variant="primary" size="sm" disabled={saveFacts.isPending} onClick={() => void proceed()}>
                保留现在的
              </Button>
            </div>
          ) : (
            <div>
              <Button variant="secondary" size="sm" icon="refresh" loading={saveFacts.isPending} onClick={() => saveFacts.mutate({ petId: created.pet_id })}>
                {factsBusy ? "再看看" : "再试一次"}
              </Button>
              <Button variant="primary" size="sm" disabled={saveFacts.isPending} onClick={() => void proceed()}>
                先继续
              </Button>
            </div>
          )}
        </div>
      ) : (
        <>
          {missing ? <p id="own-pet-missing" className="ps-own-pet-missing" role="status">{missing}</p> : factsIssue ? <p id="own-pet-missing" className="ps-own-pet-missing" role="status">生日那一项还有要改的地方；拿不准的话，把那几项改回“不知道”，以后再填。</p> : null}
          <Button type="submit" variant="primary" block loading={create.isPending || saveFacts.isPending} disabled={Boolean(missing) || Boolean(photoError) || factsIssue || Boolean(created) || photoResultUnknown || photoNotReadyNow || photoChecking || !photoScopeReady || photoSelection.busy} aria-describedby={missing || factsIssue ? "own-pet-missing" : undefined}>
            {householdId ? "把 TA 带进这个家" : "继续，去见接待员"}
          </Button>
        </>
      )}
      {error ? (
        photoDown ? (
          <div role="alert" className="ps-own-pet-photo-down">
            <p>{photoDown.text}</p>
            {(photoDown.retry && photo) || photoDown.exits ? (
              <div className="ps-own-pet-photo-down__actions">
                {photoDown.retry && photo ? (
                  <Button variant="secondary" size="sm" icon="refresh" loading={create.isPending} disabled={photoChecking || !photoScopeReady || photoSelection.busy} onClick={() => sendPhoto(photo)}>
                    {photoDown.label}
                  </Button>
                ) : null}
                {photoDown.exits ? (
                  <>
                    {photoDown.planetOnly ? null : <label htmlFor="own-pet-photo" className="ps-btn ps-btn--secondary ps-btn--sm">换一张照片</label>}
                    <Link to="/world" className="ps-btn ps-btn--ghost ps-btn--sm">先去星球看看</Link>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : error.code === "MEDIA_REJECTED" || error.code === "ALREADY_HAS_COMPANION" ? (
          <p role="alert" className="ps-form-error">
            {error.playerMessage}
          </p>
        ) : (
          <ErrorState error={error} />
        )
      ) : null}
    </form>
  );
}

export function OnboardingPage() {
  const redirect = useOnboardingRedirect();
  if (redirect) return <Navigate to={redirect} replace />;
  return (
    <Page bare className="ps-entry-page ps-own-pet-page">
      <TopBar title="认识 TA" heading={false} back={env.dataMode === "fixture" ? "/welcome" : undefined} />
      <EntryHeading step={2} kicker="入住准备 · 02 / 04" title="这是 TA。" description="先用你熟悉的样子和名字，让这里认识 TA。" />
      <div className="ps-own-pet-content">
        <OwnPetForm />
        <div className="ps-own-pet-alternative">
          <span>另一种相遇</span>
          <h2>想先认识星球上的伙伴？</h2>
          <p>也可以看看正在等待一个家的原创居民。选择之前，先了解 TA。</p>
          <Link to="/adopt">去认识他们 <span aria-hidden="true">→</span></Link>
        </div>
      </div>
    </Page>
  );
}

/** Existing-home flow uses the same upload and reception, never creates a second household. */
export function AddCompanionPage() {
  const { household, userId } = useCurrentHousehold();
  // 没起名的家与切换栏、“我们的家”同一套说法（“麦芽的家”，撞名带序号），不再是笼统的“当前家庭”。
  const labels = useHouseholdLabels(userId);
  if (!household || household.role !== "admin") {
    // 不是管理员：给一个去处，去「我们的家」看谁是管理员、请 TA 来带（UI-V-PAGES-01，总账 V-10）
    return <Page><TopBar title="添一位伙伴" back="/me" /><DisabledState title="需要家庭管理员" action={<Link className="ps-btn ps-btn--secondary" to="/households/manage">去我们的家看看谁是管理员</Link>}>只有家庭管理员可以把新伙伴带进这个家。</DisabledState></Page>;
  }
  return <Page className="ps-add-companion-page">
    <TopBar title="添一位伙伴" heading={false} back="/me" />
    <EntryHeading kicker="同一个家 · 新的伙伴" title="这个家，再认识一位 TA" description="新伙伴会先经过接待与入住，再出现在家里的切换栏。照片和名字都来自你刚提交的资料。" />
    <Card className="ps-stack ps-entry-card ps-companion-card">
      <Chip tone="leaf" icon="home">{household.name?.trim() || labels?.get(household.household_id) || householdLabel(household, 0)}</Chip>
      <p className="ps-muted">只加入这个家，不会新建家庭。现有伙伴、家园位置和库存保持原样。</p>
      <OwnPetForm householdId={household.household_id} />
    </Card>
  </Page>;
}

/**
 * 一位待领养的居民：谁（照片或同物种插画、名字、物种、性格）、梦想、来处。头像与物种标签和星球居民卡同一套
 * （ResidentPortrait、speciesName；样式 .ps-resident-portrait / .ps-world-resident__species 在 planet.css，随星球页全局加载）。
 * 两个动作分开：“认识 TA”去 TA 在星球上的居民主页，只是认识；“迎接 TA”才弹出确认，确认之后才领养，不会自动领养。
 * 没有 pet_id 的候选（还没在星球上生活）没有居民主页，只有“迎接 TA”。
 */
/** 候选的近景框：只认官方形象（photo_url 就是 identity 那张），和星球日常、居民详情同一份审过的框 */
function candidateFocus(candidate: AdoptionCandidate) {
  if (!candidate.pet_id) return null;
  return identityFaceCrop({ pet_id: candidate.pet_id, identity: candidate.identity, avatar_url: candidate.photo_url, official_avatar: candidate.official_avatar });
}

function CandidateCard({ candidate }: { candidate: AdoptionCandidate }) {
  const taken = candidate.availability !== "available";
  return (
    <Card className="ps-stack ps-entry-card ps-resident-card ps-adopt-card">
      <div className="ps-adopt-card__head">
        {/* 头像 56→96；审过头部框的官方形象用近景（UI-FIRST-IMPRESSION-116） */}
        <ResidentPortrait name={candidate.name} species={candidate.species} photoUrl={candidate.photo_url} origin={candidate.origin} size={96} focus={candidateFocus(candidate)} />
        <div className="ps-adopt-card__who">
          <div className="ps-adopt-card__name">
            <strong>{candidate.name}</strong>
            <span className="ps-world-resident__species">{speciesName(candidate.species)}</span>
            {taken ? <Chip>{candidate.availability === "adopted" ? "已有家" : "保留中"}</Chip> : null}
          </div>
          <div className="ps-muted">{candidate.personality}</div>
        </div>
      </div>
      <p className="ps-adopt-card__dream">梦想：{candidate.dream}</p>
      <div className="ps-muted">来源：{candidate.source_note ?? "未知"}</div>
      <DataOriginBadge origin={candidate.data_origin} label={candidate.data_origin === "fixture" ? "演示伙伴" : "PetSoul 原创伙伴"} />
      <AdoptFlow
        candidateId={candidate.candidate_id}
        petId={candidate.pet_id}
        name={candidate.name}
        adoptable={!taken}
        presence={candidate.presence}
        backAt={candidate.back_at}
        beside={candidate.pet_id ? (
          <Link className="ps-btn ps-btn--secondary" to={`/world/residents/${encodeURIComponent(candidate.pet_id)}`} aria-label={`认识 TA：${candidate.name}`}>
            认识 TA
          </Link>
        ) : null}
      />
    </Card>
  );
}

/**
 * 领养页空的时候（S-007，I 10-09 定「空了要有出路」）：不只说没有，给两条路——带上自己的 TA（回填资料的那一页），或先去星球看看。
 * 名单完全为空、都有家了、都还没有形象，三种都用这一块；下面已经有「都已经有家了，去星球上认识 TA 们」那一行时，不重复放去星球（UI-ADOPT-EMPTY-01）。
 */
function AdoptEmpty({ planetBelow }: { planetBelow: boolean }) {
  return (
    <EmptyState
      icon="home"
      title="现在没有在等一个家的居民"
      action={
        <div className="ps-adopt-empty-actions">
          <Link className="ps-btn ps-btn--primary" to="/onboarding">
            带上你自己的 TA
          </Link>
          {planetBelow ? null : (
            <Link className="ps-btn ps-btn--secondary" to="/world">
              先去星球看看
            </Link>
          )}
        </div>
      }
    >
      新居民住进来以后，会出现在这里。
    </EmptyState>
  );
}

export function AdoptPage() {
  const redirect = useOnboardingRedirect();
  const session = useSessionState();
  const { pets } = useServices();
  // 候选名单只有登录后才给读：没登录（会被送去 /welcome）、要去别的入住步骤时都不去读，免得访客打开回 401（6796 10-03 深色扫查）
  const canRead = env.dataMode !== "live" || (session.data?.authenticated === true && !redirect);
  const query = useQuery({ queryKey: queryKeys.adoption, queryFn: () => pets.adoptionCandidates(), enabled: canRead });
  if (redirect) return <Navigate to={redirect} replace />;
  return (
    <Page bare className="ps-entry-page">
      <TopBar title="专属领养" heading={false} subtitle="每一位只属于一个家庭" back="/onboarding" />
      <EntryHeading step={2} kicker="入住准备 · 02 / 04" title="认识一位新伙伴" description="这里的居民有自己的来处与此刻的生活。选中之后还会再让你确认，不会自动领养。" />
      <QueryView query={query} isEmpty={(list) => list.length === 0} empty={<AdoptEmpty planetBelow={false} />}>
        {(list) => {
          // 已经有家的居民不堆在待领养列表里（按钮也点不了），收成一行，去星球上认识 TA 们。
          // “去星球上认识 TA 们”用不断行空格粘成一块：窄屏只在“，”后面断行（原来 375 宽“……认识 TA / 们”，只用 keep-all 时是“……认识 / TA 们”）。
          // 只列有自己形象的候选（用户 10-03「没有形象的都不要出现在星球了」）；演示模式照旧
          const withImage = env.dataMode === "live" ? list.filter(hasOwnImage) : list;
          const open = withImage.filter((c) => c.availability !== "adopted");
          const homed = withImage.length - open.length;
          return (
            <div className="ps-stack">
              {open.length ? open.map((c) => <CandidateCard key={c.candidate_id} candidate={c} />) : <AdoptEmpty planetBelow={homed > 0} />}
              {homed ? (
                <Link className="ps-adopt-homed" to="/world">
                  <span>{open.length ? `还有 ${homed} 位居民已经有家了，` : `${homed} 位居民都已经有家了，`}{"去星球上认识\u00a0TA\u00a0们"}</span>
                  <Icon name="chevron" size={16} />
                </Link>
              ) : null}
            </div>
          );
        }}
      </QueryView>
    </Page>
  );
}
